import { z } from 'zod';
import { canonicalize } from './canonical.js';
/**
 * Domain-separated, rule-versioned SHA-256.
 *
 * Uses Web Crypto rather than node:crypto so this module works unchanged in the
 * browser — apps/web imports @vigeo/shared, and a node:crypto import would break the
 * bundle. The cost is that hashing is async.
 *
 * Every input is prefixed with a spec string, which buys two things:
 *  - domain separation, so a content digest can never collide with a controlled-text
 *    digest and a digest is never ambiguous about what it attests;
 *  - rule versioning, so if canonicalisation ever has to change, v2 digests are
 *    provably distinct and instances signed under v1 keep verifying under v1.
 */
export const CONTENT_HASH_SPEC = 'vigeo.step-content.v1';
export const TEXT_HASH_SPEC = 'vigeo.controlled-text.v1';
/** A portal token, hashed so the `tokens` container never stores a usable secret. */
export const PORTAL_TOKEN_SPEC = 'vigeo.portal-token.v1';
/** Bytes as uploaded, so a routed file can be proved identical to what arrived. */
export const FILE_HASH_SPEC = 'vigeo.file-bytes.v1';
/** A generated artifact — a flattened PDF — which is a different claim from an upload. */
export const ARTIFACT_HASH_SPEC = 'vigeo.artifact-bytes.v1';
/** A self-service reissue rate-limit bucket (#82): an address or IP, never stored as itself. */
export const REISSUE_LIMIT_SPEC = 'vigeo.reissue-limit.v1';
/** `sha256:<64 lowercase hex>` — self-describing, so the algorithm is never implicit. */
export const digestSchema = z.string().regex(/^sha256:[0-9a-f]{64}$/);
async function digest(spec, payload) {
    // A raw newline cannot occur inside canonical JSON — JSON.stringify escapes it as
    // \n — so it is an unambiguous separator between spec and payload.
    const bytes = new TextEncoder().encode(`${spec}\n${payload}`);
    const buffer = await crypto.subtle.digest('SHA-256', bytes);
    const hex = Array.from(new Uint8Array(buffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    return `sha256:${hex}`;
}
/** Canonical bytes of a definition's attested content, plus their digest. */
export async function sealContent(content) {
    const canonical = canonicalize(content);
    return { canonical, contentHash: await digest(CONTENT_HASH_SPEC, canonical) };
}
/**
 * Digest of an acknowledgment's controlled text on its own.
 *
 * Deliberately separate from the content digest: this is the evidentiary artifact
 * behind "prove this person saw exactly these words", so it must stay stable even if
 * the surrounding content schema later gains a field.
 *
 * The input must already be normalised — hashing raw input records a digest for a
 * value that differs from what was stored.
 */
export async function hashControlledText(normalizedText) {
    return digest(TEXT_HASH_SPEC, JSON.stringify(normalizedText));
}
/**
 * Digest of raw bytes. The private `digest()` takes a string, so before this there was no
 * way to hash a file at all.
 *
 * Domain-separated like everything else: the same bytes uploaded and generated produce
 * different digests, because "the person sent us this" and "we produced this" are
 * different claims and must not be interchangeable as evidence.
 */
export async function hashFileBytes(bytes, spec = FILE_HASH_SPEC) {
    const prefix = new TextEncoder().encode(`${spec}\n`);
    const payload = new Uint8Array(prefix.length + bytes.length);
    payload.set(prefix, 0);
    payload.set(bytes, prefix.length);
    const buffer = await crypto.subtle.digest('SHA-256', payload);
    const hex = Array.from(new Uint8Array(buffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    return `sha256:${hex}`;
}
/**
 * Constant-time hex comparison, for the one place a digest gates access: portal token
 * lookup (ADR-004). An early-exit compare there leaks the stored hash a byte at a time to
 * anyone who can time the response.
 *
 * Length is compared first and non-secretly — it is fixed and public for a digest.
 */
export function timingSafeEqualHex(a, b) {
    if (a.length !== b.length)
        return false;
    let difference = 0;
    for (let i = 0; i < a.length; i += 1) {
        difference |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return difference === 0;
}
/** Hash a portal token for storage and lookup. The plaintext is never persisted. */
export async function hashPortalToken(token) {
    return digest(PORTAL_TOKEN_SPEC, JSON.stringify(token));
}
/**
 * The id of a reissue rate-limit counter (#82). The bucket's key — a normalised address or a
 * client IP — goes in, and only its digest is ever written, so the counters container holds
 * no address and no IP. Domain-separated from token digests, so a counter id can never be
 * presented as, or collide with, a token lookup.
 */
export async function hashReissueLimitKey(bucket) {
    return digest(REISSUE_LIMIT_SPEC, JSON.stringify([bucket.scope, bucket.key, bucket.windowStart]));
}
export async function verifyContent(canonical, expected) {
    // Plain comparison is fine: these values are public and fixed-length. Constant-time
    // comparison belongs where a hash gates access — portal tokens, ADR-004.
    return (await digest(CONTENT_HASH_SPEC, canonical)) === expected;
}
//# sourceMappingURL=hash.js.map